import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

export default function SessionExpiredAlert() {
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const { signOut, user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const performSignOut = useCallback(async () => {
    setIsVisible(false); // Hide banner before redirect
    await signOut();
    api.resetSessionExpiredFlag();
    navigate('/');
  }, [signOut, navigate]);

  const handleSessionExpired = useCallback(() => {
    // Only show if user was logged in
    if (!user) return;

    // Save current location for return after re-login
    const currentPath = location.pathname + location.search + location.hash;
    if (currentPath !== '/' && currentPath !== '/dashboard') {
      sessionStorage.setItem('returnTo', currentPath);
    }

    setCountdown(5); // Reset countdown
    setIsVisible(true);
  }, [user, location]);

  // Listen for session-expired event
  useEffect(() => {
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [handleSessionExpired]);

  // Hide banner when user is logged out
  useEffect(() => {
    if (!user && isVisible) {
      setIsVisible(false);
    }
  }, [user, isVisible]);

  // Countdown and redirect
  useEffect(() => {
    if (!isVisible) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          performSignOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible, performSignOut]);

  const handleSignInNow = () => {
    performSignOut();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 shadow-lg animate-slide-down">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Session Expired</p>
            <p className="text-sm text-amber-100">
              Your session has expired. Redirecting to login in {countdown}s...
            </p>
          </div>
        </div>
        <button
          onClick={handleSignInNow}
          className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
        >
          Sign in now
        </button>
      </div>
    </div>
  );
}
